import { useContext, useEffect, useState, useMemo, useRef } from 'react';
import {
  ConfigContext,
  PrivacySettingsContext,
  WidgetStateContext,
} from '@/context';
import { Views } from '@/constants/views';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetFooter,
} from '@/components/ui/Sheet';
import { ConsentManagement } from './ConsentManagement';
import { ConsentCategoryDetail } from './ConsentManagement/ConsentCategoryDetail';
import { ViewContainer } from './ViewContainer';
import {
  ComplianceRequestChange,
  ComplianceRequestCopy,
  ComplianceRequestDelete,
  ComplianceRequestDoNotSell,
} from './PrivacyOptions/ComplianceRequestCreate';
import { ComplianceRequestVerify } from './PrivacyOptions/ComplianceRequestVerify';
import { DataPrivacy } from './DataPrivacy';
import { PrivacyOptionsSelector } from './PrivacyOptions/PrivacyOptionsSelector';
import { ConcordLogo } from '@/assets/icons';
import { X } from '@phosphor-icons/react';
import { mostReadable } from '@ctrl/tinycolor';
import { ConsentCategoryItem } from '@/types';
import { cn } from '@/utils/cn';

type Props = {
  zIndex: number;
};

export function PrivacyCenter({ zIndex }: Props) {
  /**
   * ConsentCategory is used in the Consent Settings tab when a user clicks on
   * 'Additional Details' in the accordion window below a cookie category.
   */
  const [consentCategory, setConsentCategory] =
    useState<ConsentCategoryItem | null>(null);

  const concordWidgetRef = useRef<HTMLDivElement>(null);
  const [showViewLabels, setShowViewLabels] = useState<boolean>(false);

  const {
    open,
    setOpen,
    setSelectedView,
    selectedView,
    consentManagementEnabled,
    privacyCenterEnabled,
    topRef,
  } = useContext(WidgetStateContext);
  const {
    consentSettingsTabTitle,
    privacyCenterTabTitle,
    poweredByConcord,
    logoUrl,
    config,
  } = useContext(ConfigContext);
  const { privacySettingsSet } = useContext(PrivacySettingsContext);

  const theme =
    config?.regionTemplate?.settings?.privacyCenter?.theme || 'classic';

  const memoizedLabels = useMemo(() => {
    const isConsentActive = privacySettingsSet;
    const labels = [];

    if (privacyCenterEnabled) {
      labels.push({
        key: Views.DataPrivacy,
        label: privacyCenterTabTitle,
        active: isConsentActive,
      });
    }
    if (consentManagementEnabled) {
      labels.push({
        key: Views.ConsentManagement,
        label: consentSettingsTabTitle,
        active: !isConsentActive,
      });
    }

    return labels;
  }, [privacySettingsSet, privacyCenterEnabled, consentManagementEnabled]);

  useEffect(() => {
    if (memoizedLabels.length > 1) {
      setShowViewLabels(true);
    }
  }, [memoizedLabels]);

  return (
    <Sheet
      className={`z-${zIndex} relative`}
      open={open}
      onOpenChange={setOpen}
    >
      <SheetContent
        ref={concordWidgetRef}
        aria-describedby={undefined}
        className={`${theme} border-pc-border flex h-full max-h-screen w-full flex-col md:w-10/12 xl:w-9/12`}
        container={topRef}
        id="concord-widget"
        side="right"
        onKeyDown={(e: React.KeyboardEvent) => {
          /* close widget if user navigates away using screen reader */
          if (!concordWidgetRef.current || e.key !== 'Tab') return;

          const focusableElements = [
            'a[href]',
            'button',
            'input',
            'select',
            'textarea',
            '[tabindex]:not([tabindex="-1"])',
            'details',
            '[contenteditable]',
            '[role="link"]',
            '[role="button"]',
          ];

          const children = concordWidgetRef.current.querySelectorAll(
            focusableElements.join(','),
          );

          const firstFocusableEl = children[0];
          const lastFocusableEl = children[children.length - 1];
          const elInFocus = document.activeElement?.shadowRoot?.activeElement;

          if (
            (elInFocus === firstFocusableEl && e.shiftKey) ||
            elInFocus === lastFocusableEl
          ) {
            setOpen(false);
          }
        }}
      >
        <SheetHeader
          className="bg-banner min-h-52 w-full px-10 md:px-16 lg:px-24"
          data-testid="cd-header-container"
        >
          <SheetTitle className="flex w-full items-center justify-between pt-8 md:pt-9">
            {logoUrl ? (
              <img
                alt="Logo"
                aria-label="Logo"
                className="my-5 max-h-10 w-auto max-w-52"
                src={logoUrl}
              />
            ) : (
              <ConcordLogo
                fontColor={mostReadable('var(--banner)', [
                  '#fff',
                  '#000',
                ])?.toHexString()}
                shapeColor1={mostReadable('var(--banner)', [
                  '#fff',
                  '#000',
                ])?.toHexString()}
                shapeColor2={mostReadable('var(--banner)', [
                  '#fff',
                  '#000',
                ])?.toHexString()}
                width={205}
              />
            )}
            <SheetClose
              className={cn(
                'text-primary hover:text-primary-hover cursor-pointer opacity-80 transition-opacity hover:scale-110 hover:opacity-100',
                theme === 'light' &&
                  'text-foreground hover:text-primary opacity-70',
              )}
              data-testid="cd-privacy-center-close-button"
            >
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </SheetClose>
          </SheetTitle>
        </SheetHeader>
        <div className="space-y-6">
          <div
            className={`rounded-card w-full bg-transparent px-10 md:px-16 lg:px-24 ${showViewLabels ? '-mt-[102.4px]' : '-mt-9'}`}
          >
            <ViewContainer
              labels={memoizedLabels}
              selectedTab={
                selectedView.includes('consent')
                  ? Views.ConsentManagement
                  : Views.DataPrivacy
              }
              showViewLabels={showViewLabels}
              onSelectedTabChange={setSelectedView}
            >
              {selectedView === Views.ComplianceRequestChange && (
                <ComplianceRequestChange
                  onCancel={() => setSelectedView(Views.DataPrivacy)}
                  onSubmit={() =>
                    setSelectedView(Views.ComplianceRequestVerify)
                  }
                />
              )}
              {selectedView === Views.ComplianceRequestCopy && (
                <ComplianceRequestCopy
                  onCancel={() => setSelectedView(Views.DataPrivacy)}
                  onSubmit={() =>
                    setSelectedView(Views.ComplianceRequestVerify)
                  }
                />
              )}
              {selectedView === Views.ComplianceRequestDelete && (
                <ComplianceRequestDelete
                  onCancel={() => setSelectedView(Views.DataPrivacy)}
                  onSubmit={() =>
                    setSelectedView(Views.ComplianceRequestVerify)
                  }
                />
              )}
              {selectedView === Views.ComplianceRequestDoNotSell && (
                <ComplianceRequestDoNotSell
                  onCancel={() => setSelectedView(Views.DataPrivacy)}
                  onSubmit={() =>
                    setSelectedView(Views.ComplianceRequestVerify)
                  }
                />
              )}
              {selectedView === Views.ComplianceRequestVerify && (
                <ComplianceRequestVerify
                  onCancel={() => setSelectedView(Views.DataPrivacy)}
                  onSubmit={() => setSelectedView(Views.DataPrivacy)}
                />
              )}
              {selectedView === Views.PrivacyOptionsSelector && (
                <PrivacyOptionsSelector
                  loading={false}
                  setSelectedView={setSelectedView}
                  onCancel={() => setSelectedView(Views.DataPrivacy)}
                />
              )}
              {selectedView === Views.DataPrivacy && (
                <DataPrivacy setSelectedView={setSelectedView} theme={theme} />
              )}
              {selectedView === Views.ConsentManagement && (
                <ConsentManagement
                  setConsentCategory={setConsentCategory}
                  setSelectedView={setSelectedView}
                />
              )}
              {selectedView === Views.ConsentCategoryDetail && (
                <ConsentCategoryDetail
                  consentCategory={consentCategory}
                  setSelectedView={setSelectedView}
                />
              )}
            </ViewContainer>
          </div>
          {poweredByConcord && (
            <SheetFooter className="px-10 md:px-16 lg:px-24">
              <a
                className="hover:no-underline"
                href="https://www.concord.tech"
                rel="noopener noreferrer"
                target="_blank"
              >
                <div className="mb-6 flex items-center justify-end">
                  <span className="text-subheader mr-2.5 text-xs italic">
                    Powered by
                  </span>
                  <div>
                    <ConcordLogo
                      fontColor="var(--subheader)"
                      shapeColor1="var(--subheader)"
                      shapeColor2="var(--subheader)"
                      width={145}
                    />
                  </div>
                </div>
              </a>
            </SheetFooter>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
